/* eslint css-modules/no-unused-class: "off" */
import React, { Fragment, useEffect, useRef, useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { Button, Label } from "reactstrap";
import ReactTable from "react-table";
import reactTableS from "react-table/react-table.css";
import ReactTooltip from "react-tooltip";
import * as commonActions from "../../../utils/actions/common";
import classNames from "classnames";
import isEqual from "lodash.isequal";
import { trim } from "../../../utils/commonFunctions";
import ISelectionUpdate from "../../../types/ISelectionUpdate";
import s from "./styles.scss";
import IPaginatedResponse from "../../../types/services/IPaginatedResponse";
import ICharity from "../../../types/services/ICharity";
import IDashboardColumn from "../../../types/IDashboardColumn";
import { validURL } from "../../../utils/helpers";
import {
  DEFAULT_TABLE_PAGE_SIZE,
  getTablePageSize,
  saveTablePageSize,
  TABLE_PAGE_SIZES,
} from "../../../utils/tablePrefs";

function unsubscribedSinceTooltip(
  isUnsubscribed: boolean,
  prettyDate: string | undefined,
  fallback: string,
  invalidLabel: string
): string {
  if (!isUnsubscribed) {
    return invalidLabel;
  }
  if (prettyDate && prettyDate !== "Not available") {
    return `Unsubscribed since ${prettyDate}`;
  }
  return fallback;
}

function findTableCell(el: HTMLElement | null): HTMLElement | null {
  let node = el;
  while (node) {
    if (node.classList && node.classList.contains("rt-td")) {
      return node;
    }
    node = node.parentElement;
  }
  return el;
}

function cellContentWidth(cell: HTMLElement): number {
  const style = window.getComputedStyle(cell);
  const left = parseFloat(style.paddingLeft) || 0;
  const right = parseFloat(style.paddingRight) || 0;
  return cell.clientWidth - left - right;
}

function CollapsingDateCell({ full, short }: { full: string; short: string }) {
  const boxRef = useRef<HTMLSpanElement>(null);
  const fullRef = useRef<HTMLSpanElement>(null);
  const [useShort, setUseShort] = useState(true);

  useEffect(() => {
    const box = boxRef.current;
    const fullEl = fullRef.current;
    if (!box || !fullEl) {
      return;
    }
    const cell = findTableCell(box);
    const update = () => {
      if (!short) {
        setUseShort(false);
        return;
      }
      const limit = cell ? cellContentWidth(cell) : box.clientWidth;
      setUseShort(fullEl.offsetWidth > limit);
    };
    update();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(update);
    if (cell) {
      observer.observe(cell);
    } else {
      observer.observe(box);
    }
    return () => {
      observer.disconnect();
    };
  }, [full, short]);

  if (!full && !short) {
    return null;
  }

  return (
    <span ref={boxRef} className={s.dateCollapse}>
      <span ref={fullRef} className={s.dateMeasure} aria-hidden="true">
        {full}
      </span>
      <span className={s.dateVisible}>{useShort ? short || full : full}</span>
    </span>
  );
}

export interface InactiveContactIndicators {
  isInactive: (original: any) => boolean;
  /** Column accessor whose cell is replaced by the inactive badge (e.g. `segments`). */
  badgeReplacementColumnAccessor: string;
  /** Column accessors whose text is italic when inactive (e.g. first and last name). */
  italicColumnAccessors?: string[];
}

export interface ColumnHeaderSortConfig {
  sortAccessor: string;
  sortAscending: boolean;
  sortableAccessors: string[];
  onSort: (columnAccessor: string) => void;
}

interface Props {
  /** Render optional details beneath an expandable row. */
  renderRowDetails?: (row: any) => React.ReactNode;
  columnNames: Array<any>;
  columnData: Array<any>;
  extraColumns: Array<IDashboardColumn>;
  checkCol: boolean;
  sortBy: string;
  selectAll: boolean;
  sortAsc: boolean;
  footerMessage: string;
  searchText?: string;
  noDataText?: string;
  from?: any;
  to?: any;
  currentCharity: ICharity;
  getCharityName?: (charityId: string) => string;
  refresh?: boolean;
  filtered?: boolean;
  hideSelection?: boolean;
  selectElement: (quantity: string, message: string) => void;
  showInformation?: (elementId: string) => void;
  buttonAction?: (elementId: string) => void;
  showUsersFunction?: (elementId: string) => void;
  showRelationFunction?: (elementId: string) => void;
  disableInfoFunction?: (elementId: string) => void;
  /** Persist page size under `viewspark.tablePrefs.{tableId}`. */
  tableId?: string;
  getResponse: (
    page: number,
    pageSize: number
  ) => Promise<IPaginatedResponse<any>>;
  selectionUpdate?: (selection: ISelectionUpdate) => void;
  refreshDashboard?: () => void;
  showPagination?: boolean;
  wipeData?: boolean;
  deactivatedClassName?: string;
  preventScrollOnFetch?: boolean;
  inactiveContactIndicators?: InactiveContactIndicators;
  columnHeaderSort?: ColumnHeaderSortConfig;
  /** When set with {@link checkCol}, renders select-all in the checkbox column header. */
  selectAllInCheckboxHeader?: boolean;
  onSelectAllCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * When set (e.g. donors list bulk select-all), clears {@link State.suppressedData}
   * when the parent clears suppressed ids so the header checkbox stays consistent.
   */
  suppressedIds?: string[];
}

interface State {
  data: Array<any>;
  selectedData: Array<any>;
  suppressedData: Array<any>;
  selected: number;
  loading: boolean;
  totalPages: number;
  sorted: any;
  filtered: any;
  totalElems: number;
  time: number;
  rows: any[];
  pageSize: number;
  expanded: Record<string, boolean>;
}

const NEW_CONTACT_WINDOW_MS = 60_000;

/** True when the donor record was created by this donation (within 60s after `receivedOn`). */
export function isNewContactDonation(donation): boolean {
  const createdOn = donation?.donor?.createdOn;
  return (
    !!createdOn &&
    createdOn >= donation.receivedOn &&
    createdOn <= donation.receivedOn + NEW_CONTACT_WINDOW_MS
  );
}

class Dashboard extends React.PureComponent<Props, State> {
  private detailsRequest = 0;
  private selectAllHeaderInputRef = React.createRef<HTMLInputElement>();

  state = {
    expanded: {} as Record<string, boolean>,
    data: [],
    selectedData: [],
    suppressedData: [],
    selected: 0,
    loading: false,
    totalPages: 0,
    sorted: [{ id: this.props.sortBy, desc: !this.props.sortAsc }],
    filtered: [{ id: "name", value: this.props.searchText }],
    totalElems: 0,
    time: 0,
    rows: this.updateTableColumns(false),
    pageSize: this.props.tableId
      ? getTablePageSize(this.props.tableId)
      : DEFAULT_TABLE_PAGE_SIZE,
  };

  componentDidMount() {
    this.updateTableColumns();
    !this.props.hideSelection &&
      this.props.selectElement(
        this.state.selected.toString(),
        this.props.footerMessage
      );
    this.syncSelectAllHeaderCheckboxIndeterminate();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: State) {
    if (this.props.renderRowDetails && prevState.data !== this.state.data) {
      this.updateTableColumns(true);
    }
    if (prevProps.columnNames !== this.props.columnNames) {
      this.updateTableColumns(true);
    }
    if (this.props.sortBy != prevProps.sortBy) {
      this.setState({
        sorted: [{ id: this.props.sortBy, desc: !this.props.sortAsc }],
      });
    }

    if (this.props.sortAsc != prevProps.sortAsc) {
      this.setState({
        sorted: [{ id: this.props.sortBy, desc: !this.props.sortAsc }],
      });
    }

    if (this.props.searchText != prevProps.searchText) {
      this.setState({
        filtered: [{ id: "name", value: this.props.searchText }],
      });
    }

    if (this.props.from != prevProps.from) {
      this.setState({
        filtered: [{ id: "name", value: this.props.from }],
      });
    }
    if (this.props.to != prevProps.to) {
      this.setState({
        filtered: [{ id: "name", value: this.props.to }],
      });
    }
    if (this.props.currentCharity != prevProps.currentCharity) {
      if (this.props.renderRowDetails) {
        this.detailsRequest++;
        this.setState({ expanded: {}, data: [] });
      }
      this.setState({
        filtered: [{ id: "name", value: this.props.currentCharity.id }],
      });
      this.setState(
        {
          selectedData: [],
          suppressedData: [],
          selected: 0,
        },
        () => {
          this.props.selectionUpdate({
            selectedIds: this.state.selectedData.map(elem => elem.id),
            suppressedIds: this.state.suppressedData.map(elem => elem.id),
            totalAmount: 0,
            selectedData: this.state.selectedData,
          });
          !prevProps.hideSelection &&
            this.props.selectElement("0", prevProps.footerMessage);
        }
      );
    }

    if (
      !isEqual(this.props.columnData, prevProps.columnData) ||
      !isEqual(this.props.extraColumns, prevProps.extraColumns) ||
      !isEqual(this.props.columnNames, prevProps.columnNames) ||
      !isEqual(
        this.props.inactiveContactIndicators,
        prevProps.inactiveContactIndicators
      ) ||
      !isEqual(this.props.columnHeaderSort, prevProps.columnHeaderSort) ||
      this.props.selectAllInCheckboxHeader !==
        prevProps.selectAllInCheckboxHeader ||
      this.props.onSelectAllCheckboxChange !==
        prevProps.onSelectAllCheckboxChange
    ) {
      this.updateTableColumns(true);
    }

    if (this.props.refresh) {
      this.setState({
        filtered: [{ id: "name", value: "refresh" }],
        loading: true,
      });
      this.props.refreshDashboard();
    }

    if (this.props.wipeData && !prevProps.wipeData) {
      this.setState(
        {
          filtered: [{ id: "name", value: "refresh" }],
          loading: true,
          selectedData: [],
          suppressedData: [],
          selected: 0,
        },
        () => {
          this.props.selectionUpdate({
            selectedIds: this.state.selectedData.map(elem => elem.id),
            suppressedIds: this.state.suppressedData.map(elem => elem.id),
            totalAmount: 0,
            selectedData: this.state.selectedData,
          });
          !prevProps.hideSelection &&
            this.props.selectElement("0", prevProps.footerMessage);
        }
      );
    }

    if (this.props.selectAll != prevProps.selectAll) {
      if (this.props.selectAll && !this.props.searchText) {
        this.setState(
          {
            selected: this.state.totalElems,
            selectedData: [],
            suppressedData: [],
          },
          () => {
            !prevProps.hideSelection &&
              this.props.selectElement(
                this.state.selected.toString(),
                prevProps.footerMessage
              );
          }
        );
      } else if (this.props.selectAll && this.props.searchText) {
        let newSelectedData = [...this.state.selectedData];
        let selectedIds = newSelectedData.map(elem => elem.id);
        this.state.data.forEach(elem => {
          if (!selectedIds.includes(elem.id)) {
            selectedIds.push(elem.id);
            newSelectedData.push(elem);
          }
        });
        this.setState(
          {
            selectedData: newSelectedData,
            selected: selectedIds.length,
            suppressedData: [],
          },
          () => {
            !prevProps.hideSelection &&
              this.props.selectElement(
                this.state.selected.toString(),
                prevProps.footerMessage
              );
          }
        );
      } else if (!this.props.selectAll && !this.props.searchText) {
        this.setState(
          {
            selected: 0,
            selectedData: [],
            suppressedData: [],
          },
          () => {
            !prevProps.hideSelection &&
              this.props.selectElement("0", prevProps.footerMessage);
          }
        );
      } else if (
        !this.props.selectAll &&
        this.props.searchText === prevProps.searchText
      ) {
        let copy = [...this.state.selectedData];
        let index = 0;
        this.state.data.forEach(elem => {
          index = copy.indexOf(elem);
          copy.splice(index, 1);
        });
        this.setState(
          {
            selectedData: copy,
            selected: copy.length,
            suppressedData: [],
          },
          () => {
            this.props.selectElement(
              this.state.selected.toString(),
              prevProps.footerMessage
            );
          }
        );
      } else {
        this.setState(
          {
            selected: this.state.selectedData.length,
          },
          () => {
            !prevProps.hideSelection &&
              this.props.selectElement(
                this.state.selected.toString(),
                prevProps.footerMessage
              );
          }
        );
      }
    }

    if (
      this.props.suppressedIds !== undefined &&
      !isEqual(this.props.suppressedIds, prevProps.suppressedIds)
    ) {
      const ids = this.props.suppressedIds || [];
      if (ids.length === 0 && this.state.suppressedData.length > 0) {
        this.setState(
          prev => ({
            suppressedData: [],
            selected:
              this.props.selectAll && prev.totalElems > 0
                ? prev.totalElems
                : prev.selected,
          }),
          () => {
            !this.props.hideSelection &&
              this.props.selectElement(
                this.state.selected.toString(),
                this.props.footerMessage
              );
            if (this.props.selectionUpdate) {
              this.props.selectionUpdate({
                selectedIds: this.state.selectedData.map(elem => elem.id),
                suppressedIds: this.state.suppressedData.map(elem => elem.id),
                totalAmount: this.state.totalElems,
                selectedData: this.state.selectedData,
              });
            }
            this.syncSelectAllHeaderCheckboxIndeterminate();
          }
        );
      }
    }

    this.syncSelectAllHeaderCheckboxIndeterminate();
  }

  private selectAllHeaderCheckboxState(): {
    fullyChecked: boolean;
    headerPartial: boolean;
    labelClass: string;
  } {
    const suppressedLen = this.state.suppressedData.length;
    const fullyChecked = this.props.selectAll && suppressedLen === 0;
    const headerPartial =
      (this.props.selectAll && suppressedLen > 0) ||
      (!this.props.selectAll &&
        this.state.selected > 0 &&
        this.state.selected < this.state.totalElems);
    const labelClass = fullyChecked
      ? s.checkAll
      : headerPartial
      ? s.checkSome
      : s.noCheck;
    return { fullyChecked, headerPartial, labelClass };
  }

  private syncSelectAllHeaderCheckboxIndeterminate(): void {
    if (
      !this.props.selectAllInCheckboxHeader ||
      !this.props.onSelectAllCheckboxChange
    ) {
      return;
    }
    const el = this.selectAllHeaderInputRef.current;
    if (!el) {
      return;
    }
    const { fullyChecked, headerPartial } = this.selectAllHeaderCheckboxState();
    el.indeterminate = headerPartial && !fullyChecked;
  }

  checkElem(elem) {
    let total = 0;
    let newData = [];
    let isSupp = false;
    if (this.getCheck(elem) && this.props.selectAll) {
      total = this.state.selected - 1;
      newData = this.state.suppressedData.concat([elem]);
      isSupp = true;
    } else if (this.getCheck(elem) && !this.props.selectAll) {
      total = this.state.selected - 1;
      newData = [...this.state.selectedData];
      let index = newData.indexOf(elem);
      newData.splice(index, 1);
    } else if (!this.getCheck(elem) && this.props.selectAll) {
      total = this.state.selected + 1;
      newData = [...this.state.suppressedData];
      let index = newData.indexOf(elem);
      newData.splice(index, 1);
      isSupp = true;
    } else {
      total = this.state.selected + 1;
      newData = this.state.selectedData.concat([elem]);
    }
    this.setState(
      {
        selectedData: !isSupp ? newData : this.state.selectedData,
        selected: total,
        suppressedData: isSupp ? newData : this.state.suppressedData,
      },
      () => {
        !this.props.hideSelection &&
          this.props.selectElement(total.toString(), this.props.footerMessage);
        if (this.props.selectionUpdate) {
          this.props.selectionUpdate({
            selectedIds: this.state.selectedData.map(elem => elem.id),
            suppressedIds: this.state.suppressedData.map(elem => elem.id),
            totalAmount: this.state.totalElems,
            selectedData: this.state.selectedData,
          });
        }
      }
    );
  }

  getCheck = elem => {
    return this.props.selectAll
      ? !this.state.suppressedData.map(elem => elem.id).includes(elem.id)
      : this.state.selectedData.map(elem => elem.id).includes(elem.id);
  };

  buttonAction(value: any) {
    if (this.props.buttonAction) {
      this.props.buttonAction(value);
    }
  }

  showInfo(value: any) {
    if (this.props.showInformation) this.props.showInformation(value);
  }

  disableInfo(value: any) {
    if (this.props.disableInfoFunction)
      return this.props.disableInfoFunction(value);
    return false;
  }

  showUsers(value: any) {
    if (this.props.showUsersFunction) this.props.showUsersFunction(value);
  }

  showRelation(value: any) {
    if (this.props.showRelationFunction) this.props.showRelationFunction(value);
  }

  renderNameList = (elem, title: string, tipPrefix: string) => {
    const names: string[] = Array.isArray(elem.value)
      ? elem.value
      : elem.value
      ? [elem.value]
      : [];
    if (names.length === 0) {
      return "";
    }
    const tipId = `${tipPrefix}-${elem.original.id}`;
    return (
      <span>
        <span data-tip data-for={tipId} className={s.dataEllipsis}>
          {names.join(", ")}
        </span>
        <ReactTooltip id={tipId} type="info" place="bottom" effect="float">
          <span>
            {title}
            <br />
            <ul className={s.segmentTooltip}>
              {names.map((name, index) => (
                <li key={`${name}_${index}`}>{name}</li>
              ))}
            </ul>
          </span>
        </ReactTooltip>
      </span>
    );
  };

  getElementShow = (elem, columnName) => {
    if (this.props.renderRowDetails && columnName === "donor.name") {
      const donor = elem.original.donor;
      const label =
        [donor?.name, donor?.email, donor?.phoneNumber].find(
          value => value && value.trim()
        ) || "";
      if (!isNewContactDonation(elem.original)) return label;
      return (
        <span>
          {label}
          <span
            className={classNames(
              s.frequencyPill,
              s.firstMonthlyPill,
              s.newContactPill
            )}
          >
            New
          </span>
        </span>
      );
    }
    if (this.props.renderRowDetails && columnName === "medium") {
      const medium = typeof elem.value === "string" ? elem.value.trim() : "";
      if (!medium) return "";
      const mediumClasses = {
        web: s.webMediumPill,
        email: s.emailMediumPill,
        share: s.shareMediumPill,
        sms: s.messageMediumPill,
        mms: s.messageMediumPill,
      };
      const label = ["sms", "mms"].includes(medium.toLowerCase())
        ? medium.toUpperCase()
        : medium.charAt(0).toUpperCase() + medium.slice(1);
      return (
        <span
          className={classNames(
            s.frequencyPill,
            mediumClasses[medium.toLowerCase()]
          )}
        >
          {label}
        </span>
      );
    }
    if (typeof elem.value === "boolean") {
      return elem.value ? "yes" : "no";
    } else if (columnName === "thankYouAssignments") {
      const assignments = elem.value || {};
      const segmentNames = assignments.segmentNames || [];
      if (!assignments.isGlobal && segmentNames.length === 0) {
        return "";
      }
      const tipId = `ty${elem.original.id}`;
      return (
        <span>
          <span data-tip data-for={tipId} className={s.thankYouAssignments}>
            {assignments.isGlobal && (
              <span className={s.globalChip}>Global</span>
            )}
            {segmentNames.map((name, index) => (
              <span key={`${name}_${index}`} className={s.segmentChip}>
                {name}
              </span>
            ))}
          </span>
          <ReactTooltip id={tipId} type="info" place="bottom" effect="float">
            <span>
              Thank you segments:
              <br />
              <ul className={s.segmentTooltip}>
                {assignments.isGlobal && <li>Global</li>}
                {segmentNames.map((name, index) => (
                  <li key={`${name}_${index}`}>{name}</li>
                ))}
              </ul>
            </span>
          </ReactTooltip>
        </span>
      );
    } else if (columnName === "notifiedTo") {
      return this.renderNameList(elem, "Notified to:", "notified");
    } else if (columnName === "thankYouUsers") {
      return this.renderNameList(elem, "Thank you users:", "tyusers");
    } else if (columnName === "segments") {
      const reducer = (accumulator, currentValue, index, array) =>
        accumulator +
        currentValue.name +
        (index < array.length - 1 ? ", " : "");
      return !elem.value ? (
        ""
      ) : (
        <span>
          <span data-tip data-for={`tp${elem.original.id}`}>
            {elem.value.reduce(reducer, "")}
          </span>
          <ReactTooltip
            id={`tp${elem.original.id}`}
            type="info"
            place="bottom"
            effect="float"
          >
            {elem.value && elem.value.length > 0 && (
              <span>
                Segments:
                <br />
                <ul className={s.segmentTooltip}>
                  {elem.value.map((s, index) => (
                    <li key={`${s.name}_${index}`}>{s.name}</li>
                  ))}
                </ul>
              </span>
            )}
          </ReactTooltip>
        </span>
      );
    } else if (columnName === "donorCount") {
      return (
        <span id={elem.original.id} key={elem.original.id}>
          <span data-tip data-for={`tp${elem.original.id}`}>
            {elem.value}
          </span>
          <ReactTooltip
            id={`tp${elem.original.id}`}
            type="info"
            place="bottom"
            effect="float"
          >
            <span id={elem.original.id + "UPD"} key={elem.original.id + "UPD"}>
              Updated every day
            </span>
          </ReactTooltip>
        </span>
      );
    } else if (columnName === "name") {
      let render = [];
      let split = elem.value.split("\n");
      split.forEach((splitE, index) => {
        render.push(
          index == 0 ? (
            <span key={"spanName" + index}>{splitE}</span>
          ) : (
            <p key={"spanName" + index}>{splitE}</p>
          )
        );
      });
      return <span className={s.noMargin}>{render}</span>;
    } else if (columnName.endsWith("phoneNumber")) {
      return trim(elem.value, "I");
    } else if (columnName === "payMethod") {
      if (!elem.value) return "";
      return elem.value.charAt(0).toUpperCase() + elem.value.slice(1);
    } else if (columnName === "frequencyPretty") {
      if (this.props.renderRowDetails) {
        const firstMonthly =
          elem.original.frequency === 2 && elem.original.isFirst;
        const label = firstMonthly ? "Monthly (New!)" : elem.value;
        return (
          <span
            className={classNames(
              s.frequencyPill,
              elem.original.frequency === 1 && s.oneTimePill,
              elem.original.frequency === 2 &&
                (firstMonthly ? s.firstMonthlyPill : s.monthlyPill)
            )}
          >
            {firstMonthly && (
              <span className={s.newMonthlySpark} aria-hidden="true">
                ✦
              </span>
            )}
            {label}
          </span>
        );
      }
      if (elem.original.frequency === 2 && elem.original.isFirst) {
        return "Monthly (1st)";
      }
      return elem.value;
    } else if (columnName === "amountPretty") {
      if (elem.original.feesAssumed) {
        return (
          <span>
            <span
              className={s.fees}
              data-tip
              data-for={`tp${elem.original.id}`}
            >
              {elem.value}
            </span>
            <ReactTooltip
              id={`tp${elem.original.id}`}
              type="info"
              place="bottom"
              effect="float"
            >
              <span>Fee covered</span>
            </ReactTooltip>
          </span>
        );
      }
      return elem.value;
    } else if (columnName === "charityId") {
      if (this.props.getCharityName) {
        return this.props.getCharityName(elem.value);
      } else {
        return elem.value;
      }
    } else if (
      columnName.toLowerCase().endsWith("url") &&
      validURL(elem.value)
    ) {
      return (
        <a href={elem.value} target="_blank">
          {elem.value}
        </a>
      );
    }

    return elem.value;
  };

  static getColWidth(colName: string) {
    if (colName === "App") {
      return 15;
    } else if (colName === "Hierarchy") {
      return 40;
    } else if (
      colName === "Email" ||
      colName === "Name" ||
      colName === "ViewSpark" ||
      colName === "TY Segments" ||
      colName === "TY Users" ||
      colName === "Notified To"
    ) {
      return 150;
    } else if (colName === "Mobile #") {
      return 70;
    } else if (colName === "Contact #") {
      return 45;
    } else if (colName === "Medium") {
      return 40;
    } else if (colName === "Frequency") {
      return 60;
    } else if (colName === "Payment") {
      return 40;
    } else if (colName === "Segment") {
      return 150;
    } else if (colName === "# of Contacts") {
      return 30;
    } else if (colName === "Date Created" || colName === "Last Update") {
      return 50;
    }

    return 80;
  }

  private contentColumnWidth(
    accessor: string,
    label: string
  ): number | undefined {
    if (
      !this.props.renderRowDetails ||
      ![
        "donor.name",
        "amountPretty",
        "frequencyPretty",
        "receivedPretty",
        "campaign",
        "medium",
      ].includes(accessor) ||
      typeof document === "undefined"
    )
      return undefined;
    const context = document.createElement("canvas").getContext("2d");
    if (!context) return undefined;
    context.font = "600 13px Roboto, sans-serif";
    let width = context.measureText(label).width + 40;
    for (const donation of this.state?.data || []) {
      let value = donation[accessor] || "";
      let extra = 22;
      context.font = "400 14px Roboto, sans-serif";
      if (accessor === "donor.name")
        value =
          [
            donation.donor?.name,
            donation.donor?.email,
            donation.donor?.phoneNumber,
          ].find(text => text && text.trim()) || "";
      if (accessor === "donor.name" && isNewContactDonation(donation))
        extra += 50;
      if (accessor === "amountPretty" && donation.feesAssumed) extra += 8;
      if (accessor === "frequencyPretty" || accessor === "medium") {
        context.font = "600 12px Roboto, sans-serif";
        extra += 14;
        if (
          accessor === "frequencyPretty" &&
          donation.frequency === 2 &&
          donation.isFirst
        ) {
          value = "Monthly (New!)";
          extra += 16;
        }
        if (accessor === "medium")
          value = String(value)
            .trim()
            .toUpperCase();
      }
      width = Math.max(width, context.measureText(String(value)).width + extra);
    }
    return Math.min(320, Math.ceil(width));
  }

  private renderColumnHeader(
    accessor: string,
    headerLabel: string
  ): JSX.Element {
    const ch = this.props.columnHeaderSort;
    const label = headerLabel ?? "";
    if (!ch) {
      return <span className={s.names}>{label}</span>;
    }
    const sortable = ch.sortableAccessors.includes(accessor);
    const active = ch.sortAccessor === accessor;
    return (
      <span
        className={classNames(s.names, sortable && s.sortableColumnHeader)}
        onClick={
          sortable
            ? e => {
                e.stopPropagation();
                ch.onSort(accessor);
              }
            : undefined
        }
        onKeyDown={
          sortable
            ? e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  ch.onSort(accessor);
                }
              }
            : undefined
        }
        role={sortable ? "button" : undefined}
        tabIndex={sortable ? 0 : undefined}
      >
        {sortable ? (
          <span className={s.columnHeaderLabel}>{label}</span>
        ) : (
          label
        )}
        {sortable && active && (
          <i
            className={classNames(
              "fas",
              ch.sortAscending ? "fa-sort-up" : "fa-sort-down",
              s.columnSortIcon
            )}
            aria-hidden
          />
        )}
      </span>
    );
  }

  getColHtml = (col: IDashboardColumn, elem: any) => {
    let tooltip = (
      <ReactTooltip
        id={col.id + "Tip" + elem.index}
        type="info"
        place="top"
        effect="float"
      >
        {col.type == "functionalIcon" && col.hasTooltip
          ? col.tooltipTextFunction(elem)
          : col.tooltipText}
      </ReactTooltip>
    );

    if (col.type === "button") {
      return (
        <div className={s.centered}>
          <Button
            data-tip
            data-for={col.id + "Tip" + elem.index}
            color="primary"
            onClick={() => col.showFunctions(elem.original)}
          >
            {col.text}
          </Button>
          {col.hasTooltip && tooltip}
        </div>
      );
    } else if (col.type === "label") {
      return (
        <div
          className={s.centered}
          onClick={() => {
            !(col.isDisabled && col.isDisabled(elem.original))
              ? col.showFunctions(elem.original)
              : undefined;
          }}
        >
          <Label
            data-tip
            data-for={col.id + "Tip" + elem.index}
            className={classNames(
              col.isDisabled && col.isDisabled(elem.original)
                ? s[col.disabledClassName]
                : s.cursorPointer,
              s[col.className]
            )}
          >
            {col.text}
          </Label>
          {col.hasTooltip && tooltip}
        </div>
      );
    } else {
      if (col.hideIconWhen?.(elem.original)) {
        return (
          <div
            className={s.extraIconPlaceholder}
            style={{ width: col.width }}
            aria-hidden
          />
        );
      }
      return (
        <div
          className={s.centered}
          onClick={() => {
            col.showFunctions(elem.original);
          }}
        >
          <i
            data-tip
            data-for={col.id + "Tip" + elem.index}
            className={classNames(
              "fas",
              col.type == "functionalIcon"
                ? col.faIconFunction(elem)
                : col.faIcon,
              col.className === "customColUsersIcon"
                ? s.customColUsersIcon
                : s[col.className],
              s.cursorPointer,
              col.isDeactivated &&
                col.isDeactivated(elem.original) &&
                col.deactivatedClassName &&
                (col.deactivatedClassName === "customColUsersIconDeactivated"
                  ? s.customColUsersIconDeactivated
                  : s[col.deactivatedClassName])
            )}
          >
            &nbsp;
          </i>
          {col.hasTooltip && tooltip}
        </div>
      );
    }
  };

  mapExtraCol = (col: IDashboardColumn) => {
    const width = (col.width || 40) + 20;
    return {
      Header: "",
      columns: [
        {
          id: col.id,
          accessor: "",
          width,
          minWidth: width,
          maxWidth: width,
          className: s.extraColCell,
          headerClassName: s.extraColCell,
          Cell: elem => {
            return this.getColHtml(col, elem);
          },
        },
      ],
    };
  };

  getExtraCols = (): Array<any> => {
    return this.props.extraColumns && this.props.extraColumns.length > 0
      ? this.props.extraColumns.map(this.mapExtraCol)
      : [];
  };

  private renderSelectAllCheckboxHeader = (): JSX.Element => {
    const { fullyChecked, labelClass } = this.selectAllHeaderCheckboxState();
    return (
      <div
        className={s.selectAllHeaderCheck}
        onClick={e => e.stopPropagation()}
      >
        <div className={s.checkBoxDiv}>
          <input
            ref={this.selectAllHeaderInputRef}
            id="dashboard-select-all"
            type="checkbox"
            className="checkbox"
            checked={fullyChecked}
            onChange={this.props.onSelectAllCheckboxChange}
          />
          <label className={labelClass} htmlFor="dashboard-select-all" />
        </div>
      </div>
    );
  };

  updateTableColumns(updateState: boolean = true) {
    const checkboxHeader =
      this.props.selectAllInCheckboxHeader &&
      this.props.onSelectAllCheckboxChange
        ? () => this.renderSelectAllCheckboxHeader()
        : "";
    let rows: Array<any> = this.props.checkCol
      ? [
          {
            Header: "",
            columns: [
              {
                Header: checkboxHeader,
                id: "checkbox",
                accessor: "",
                minWidth: 35,
                Cell: elem => {
                  return (
                    <div
                      key={elem.original.id + "checkbox"}
                      onClick={() => this.checkElem(elem.original)}
                      className={s.checkBoxDiv}
                    >
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={this.getCheck(elem.original)}
                        onChange={() => {}}
                      />
                      <label
                        htmlFor="checkbox"
                        className={
                          this.getCheck(elem.original) ? s.checkAll : s.noCheck
                        }
                      />
                    </div>
                  );
                },
              },
            ],
          },
        ]
      : [];

    if (this.props.renderRowDetails) {
      rows.splice(this.props.checkCol ? 1 : 0, 0, {
        id: "details",
        expander: true,
        width: 44,
        className: s.expandCell,
        Header: "",
        Expander: row => (
          <React.Fragment>
            <button
              type="button"
              className={s.expandButton}
              data-tip
              data-for={`donation-expand-${row.original.id}`}
              aria-label={`${
                row.isExpanded ? "Hide" : "Show"
              } donation details`}
              aria-expanded={Boolean(row.isExpanded)}
              aria-controls={`donation-details-${row.original.id}`}
              onClick={event => {
                event.stopPropagation();
                this.toggleRowDetails(row);
              }}
            >
              <i
                aria-hidden="true"
                className={`fas fa-${row.isExpanded ? "minus" : "plus"}`}
              />
            </button>
            <ReactTooltip
              id={`donation-expand-${row.original.id}`}
              effect="solid"
              place="top"
            >
              Click to see more info
            </ReactTooltip>
          </React.Fragment>
        ),
      });
    }

    let i;
    for (i = 0; i < this.props.columnNames.length; i++) {
      let current = this.props.columnData[i];
      const nameEntry = this.props.columnNames[i];
      if (!nameEntry || current === undefined) {
        continue;
      }
      if (!nameEntry.isDisabled) {
        const headerLabel = nameEntry.key;
        rows.push({
          Header: () => this.renderColumnHeader(current, headerLabel),
          accessor: this.props.columnData[i],
          minWidth: Dashboard.getColWidth(nameEntry.key),
          width: this.contentColumnWidth(current, headerLabel),
          className: nameEntry.hasCollapsingDate ? s.noCellEllipsis : undefined,
          Cell: elem => {
            if (current.endsWith("phoneNumber"))
              return this.renderPhoneNumberCell(elem, current, i);

            if (current.endsWith("email"))
              return this.renderEmailCell(elem, current, i);

            return this.renderDefaultDataCell(elem, current, i, nameEntry);
          },
          headerClassname: s.names,
        });
      }
    }

    let extraCols = this.getExtraCols ? this.getExtraCols() : [];

    rows = rows.concat(extraCols);

    if (updateState) {
      this.setState({
        rows: rows,
      });
    }

    return rows;
  }

  private renderDefaultDataCell(
    elem: any,
    current: string,
    columnIndex: number,
    nameEntry?: { hasCellTooltip?: boolean; hasCollapsingDate?: boolean }
  ): JSX.Element {
    const ind = this.props.inactiveContactIndicators;
    const inactive = ind?.isInactive(elem.original) === true;
    const replaceWithBadge =
      inactive && ind?.badgeReplacementColumnAccessor === current;
    const useItalic =
      inactive &&
      ind?.italicColumnAccessors?.includes(current) &&
      !replaceWithBadge;

    let content: React.ReactNode;
    if (replaceWithBadge) {
      content = (
        <span className={s.inactiveBadge} aria-label="Deactivated contact">
          Deactivated
        </span>
      );
    } else {
      const raw = this.getElementShow(elem, current);
      const tooltipText = typeof raw === "string" ? raw.trim() : "";
      if (nameEntry && nameEntry.hasCollapsingDate) {
        const full =
          (elem.original && elem.original.receivedPretty) || tooltipText;
        const short =
          (elem.original && elem.original.receivedPrettyShort) || "";
        content = <CollapsingDateCell full={full} short={short} />;
      } else if (nameEntry && nameEntry.hasCellTooltip && tooltipText) {
        const tipId = `cell-tip-${this.props.tableId || "dashboard"}-${
          elem.original.id
        }-${current}`;
        content = (
          <Fragment>
            <span data-tip data-for={tipId} className={s.dataEllipsis}>
              {raw}
            </span>
            <ReactTooltip id={tipId} type="info" place="top" effect="float">
              {tooltipText}
            </ReactTooltip>
          </Fragment>
        );
      } else if (useItalic) {
        const tipId = `deactivated-name-${elem.original.id}-${columnIndex}`;
        content = (
          <Fragment>
            <span
              data-tip
              data-for={tipId}
              className={s.inactiveNameTooltipWrap}
            >
              <span className={s.inactiveItalic}>{raw}</span>
            </span>
            <ReactTooltip id={tipId} type="info" place="top" effect="float">
              This contact has been deactivated
            </ReactTooltip>
          </Fragment>
        );
      } else {
        content = raw;
      }
    }

    return (
      <span
        key={elem.original.id + "_" + columnIndex}
        className={classNames(
          s.data,
          nameEntry && nameEntry.hasCollapsingDate && s.dataFill,
          replaceWithBadge && s.dataCellDeactivatedBadge,
          elem.original.deactivated &&
            this.props.deactivatedClassName &&
            (this.props.deactivatedClassName === "deactivatedLabel"
              ? s.deactivatedLabel
              : s[this.props.deactivatedClassName])
        )}
      >
        {content}
      </span>
    );
  }

  private renderPhoneNumberCell(
    elem: any,
    columnKey: string,
    columnIndex: number
  ): JSX.Element {
    const isInvalidPhoneCell =
      elem.value?.[0] === "I" || elem.original?.phoneNumberValid === false;
    const isSmsUnsubscribed = elem.original?.smsUnsubscribed;
    const tooltipId = `invalid-phone-${elem.original.id}-${columnIndex}`;

    return (
      <Fragment key={`${elem.original.id}_${columnIndex}`}>
        <span
          id={tooltipId}
          data-tip
          data-for={tooltipId}
          className={classNames(
            elem.original.smsUnsubscribed
              ? s.unsubscribedContactMethod
              : isInvalidPhoneCell
              ? s.invalidContactMethod
              : s.data
          )}
        >
          {this.getElementShow(elem, columnKey)}
        </span>

        {(isInvalidPhoneCell || isSmsUnsubscribed) && (
          <ReactTooltip id={tooltipId} type="info" place="top" effect="float">
            {unsubscribedSinceTooltip(
              isSmsUnsubscribed,
              elem.original?.smsUnsubscribedPretty,
              "MMS unsubscribed",
              "Phone currently marked invalid"
            )}
          </ReactTooltip>
        )}
      </Fragment>
    );
  }

  private renderEmailCell(
    elem: any,
    columnKey: string,
    columnIndex: number
  ): JSX.Element {
    const isInvalidEmailCell = elem.original?.emailValid === false;
    const isEmailUnsubscribed = elem.original?.emailUnsubscribed;
    const tooltipId = `invalid-email-${elem.original.id}-${columnIndex}`;

    return (
      <Fragment key={`${elem.original.id}_${columnIndex}`}>
        <span
          id={tooltipId}
          data-tip
          data-for={tooltipId}
          className={classNames(
            isEmailUnsubscribed
              ? s.unsubscribedContactMethod
              : isInvalidEmailCell
              ? s.invalidContactMethod
              : s.data
          )}
        >
          {this.getElementShow(elem, columnKey)}
        </span>

        {(isInvalidEmailCell || isEmailUnsubscribed) && (
          <ReactTooltip id={tooltipId} type="info" place="top" effect="float">
            {unsubscribedSinceTooltip(
              isEmailUnsubscribed,
              elem.original?.emailUnsubscribedPretty,
              "Email unsubscribed",
              "Email currently marked invalid"
            )}
          </ReactTooltip>
        )}
      </Fragment>
    );
  }

  sizeOptions = TABLE_PAGE_SIZES;

  onPageSizeChange = (pageSize: number) => {
    this.setState({ pageSize });
    if (this.props.tableId) {
      saveTablePageSize(this.props.tableId, pageSize);
    }
  };

  fetchData = state => {
    const request = ++this.detailsRequest;
    this.setState({ loading: true, expanded: {} });
    this.props
      .getResponse(state.page, state.pageSize)
      .then(response => {
        if (this.props.renderRowDetails && request !== this.detailsRequest)
          return;
        if (response.time > this.state.time) {
          this.setState(
            {
              loading: false,
              totalPages: response.pageAmount,
              data: response.result,
              totalElems: response.totalAmount,
              time: response.time,
            },
            () => {
              if (this.props.selectionUpdate) {
                this.props.selectionUpdate({
                  totalAmount: this.state.totalElems,
                  selectedIds: this.state.selectedData.map(elem => elem.id),
                  suppressedIds: this.state.suppressedData.map(elem => elem.id),
                  selectedData: this.state.selectedData,
                });
              }
            }
          );

          if (!this.props.preventScrollOnFetch) {
            window.scrollTo(0, 0);
          }
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        if (this.props.renderRowDetails && request !== this.detailsRequest)
          return;
        this.setState({ loading: false });
      });
  };

  private toggleRowDetails = (row: any) => {
    if (this.state.loading) return;
    const key = row.nestingPath.join(".");
    this.setState(previous => ({
      expanded: { ...previous.expanded, [key]: !previous.expanded[key] },
    }));
  };

  tableClassName = classNames("-striped -highlight", s.table);

  render() {
    return (
      <div className={s.table}>
        <ReactTable
          expanded={
            this.props.renderRowDetails ? this.state.expanded : undefined
          }
          onExpandedChange={expanded => this.setState({ expanded })}
          SubComponent={
            this.props.renderRowDetails
              ? row => (
                  <div id={`donation-details-${row.original.id}`}>
                    {this.props.renderRowDetails(row.original)}
                  </div>
                )
              : undefined
          }
          getTdProps={
            this.props.renderRowDetails
              ? (_, __, column) => ({
                  "data-row-toggle": this.props.columnData.includes(column.id)
                    ? "true"
                    : undefined,
                })
              : undefined
          }
          getTrProps={
            this.props.renderRowDetails
              ? (_, row) => ({
                  className: isNewContactDonation(row?.original)
                    ? s.newContactRow
                    : row?.original?.frequency === 2 && row.original.isFirst
                    ? s.newMonthlyRow
                    : undefined,
                  onClick: event => {
                    if (
                      !row ||
                      this.state.loading ||
                      !event.target.closest("[data-row-toggle='true']") ||
                      event.target.closest(
                        "a, button, input, label, [role='button']"
                      )
                    ) {
                      return;
                    }
                    this.toggleRowDetails(row);
                  },
                })
              : undefined
          }
          data={this.state.data}
          columns={this.state.rows}
          loading={this.state.loading}
          defaultPageSize={this.state.pageSize}
          pageSize={this.state.pageSize}
          onPageSizeChange={this.onPageSizeChange}
          noDataText={
            this.props.noDataText
              ? !this.state.loading
                ? this.props.noDataText
                : ""
              : "No Data Found"
          }
          className={this.tableClassName}
          sortable={false}
          filterable={false}
          showPagination={this.props.showPagination}
          pageSizeOptions={this.sizeOptions}
          showPageJump={false}
          pages={this.state.totalPages}
          sorted={this.state.sorted}
          filtered={this.state.filtered}
          manual
          onFetchData={this.fetchData}
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { common } = state;
  return {
    refresh: common.refresh,
  };
};

const mapDispatchToProps = dispatch => ({
  selectElement: (quantity = null, message = null) => {
    dispatch(commonActions.selectElement(quantity, message));
  },
  refreshDashboard: () => {
    dispatch(commonActions.refreshDashboard());
  },
});

export default connect<Props>(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(s, reactTableS)(Dashboard));
